import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'mgr_cart_items';
const DATES_STORAGE_KEY = 'mgr_rental_dates';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const [rentalDates, setRentalDates] = useState(() => {
    try {
      const saved = localStorage.getItem(DATES_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          start: parsed.start ? new Date(parsed.start) : null,
          end: parsed.end ? new Date(parsed.end) : null,
        };
      }
    } catch {}
    return { start: null, end: null };
  });

  // Persist items to localStorage
  useEffect(() => {
    try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items)); }
    catch {}
  }, [items]);

  // Persist dates to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DATES_STORAGE_KEY, JSON.stringify({
        start: rentalDates.start ? rentalDates.start.toISOString() : null,
        end: rentalDates.end ? rentalDates.end.toISOString() : null,
      }));
    } catch {}
  }, [rentalDates]);

  const addItem = (product, variant = null) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(item =>
        item.id === product.id && item.variant?.id === variant?.id
      );
      if (existingIndex >= 0) return prev;
      return [...prev, { ...product, variant, quantity: 1 }];
    });
  };

  const removeItem = (productId, variantId = null) => {
    setItems(prev => prev.filter(item =>
      !(item.id === productId && item.variant?.id === variantId)
    ));
  };

  const clearCart = () => {
    setItems([]);
    setRentalDates({ start: null, end: null });
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(DATES_STORAGE_KEY);
    } catch {}
  };

  const setDates = (start, end) => {
    setRentalDates({ start, end });
  };

  const calculateTotal = () => {
    if (!rentalDates.start || !rentalDates.end) return 0;
    const days = Math.ceil((rentalDates.end - rentalDates.start) / (1000 * 60 * 60 * 24)) + 1;
    return items.reduce((sum, item) => {
      const price = parseFloat(item.price);
      return sum + (price * days);
    }, 0);
  };

  const getRentalDays = () => {
    if (!rentalDates.start || !rentalDates.end) return 0;
    return Math.ceil((rentalDates.end - rentalDates.start) / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <CartContext.Provider value={{
      items,
      rentalDates,
      addItem,
      removeItem,
      clearCart,
      setDates,
      calculateTotal,
      getRentalDays,
      itemCount: items.length
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
